/*
 * <wholesale-order> — bulk-order list for sections/main-collection.liquid (setting `wholesale`).
 * Markup: snippets/wholesale-order-{list,product-rows}.liquid. Helpers: wholesale-order-cart.js.
 * Not Dawn's quick-order-list.js: that is bound to ONE product and re-renders its own section.
 *
 * Model (read before changing):
 * - Rows live in tbody#collection-products, which collections.js REPLACES (filters) or APPENDS
 *   to (load-more). So: no per-row listeners — everything is delegated from this element — and a
 *   MutationObserver re-syncs whenever rows arrive.
 * - `pending` (variantId -> {qty, price, productId, productTitle, title}) is the not-yet-added
 *   selection. It intentionally survives filter changes so a buyer can build an order across
 *   filters/pages and add it in one go; the bar discloses how many selected units aren't shown.
 * - A qty input means "add this many", never "set the cart line to this many".
 * - Cart writes are serialised through `this.chain` so rapid clicks can't race each other, and
 *   each product is its own /cart/add request (see runAdd).
 * - After every write we re-read /cart.js (source of truth) rather than trusting the add
 *   response, which only lists the lines that were just added.
 */
'use strict';

if (!customElements.get('wholesale-order')) {
  const WC = window.WholesaleCart;

  class WholesaleOrder extends HTMLElement {
    connectedCallback() {
      this.strings = JSON.parse(this.querySelector('[data-ws-strings]').textContent);
      this.tbody = this.querySelector('#collection-products');
      this.pending = new Map();
      this.chain = Promise.resolve();
      this.busy = false;

      this.onQty = this.onQty.bind(this);
      this.addEventListener('input', this.onQty);
      this.addEventListener('change', this.onQty);
      this.addEventListener('click', this.onClick.bind(this));

      this.observer = new MutationObserver(() => this.syncRows());
      this.observer.observe(this.tbody, { childList: true });

      // Other components (cart drawer qty changes, quick view) also mutate the cart.
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.source === 'wholesale-order') return;
        this.refreshCart();
      });
      this.syncRows();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.unsubscribe?.();
    }

    rowFor(id) {
      return this.tbody.querySelector(`[data-ws-variant="${CSS.escape(String(id))}"]`);
    }

    onQty(event) {
      const input = event.target.closest('.ws-qty-input');
      if (!input) return;
      const row = input.closest('[data-ws-variant]');
      let qty = parseInt(input.value, 10);
      if (!Number.isFinite(qty) || qty < 0) qty = 0;
      // Normalise only on commit; rewriting the value on every keystroke fights the user's typing.
      if (event.type === 'change' && input.value !== String(qty)) input.value = qty;

      const id = row.dataset.wsVariant;
      if (qty > 0) {
        this.pending.set(id, {
          qty,
          price: WC.unitPrice(row, qty),
          productId: row.dataset.wsProductId,
          productTitle: row.dataset.productTitle,
          title: row.dataset.title,
        });
      } else {
        this.pending.delete(id);
      }
      row.classList.toggle('ws-variant--pending', qty > 0);
      this.renderSelection();
    }

    onClick(event) {
      const addBtn = event.target.closest('[data-ws-add]');
      if (addBtn) {
        if (addBtn.getAttribute('aria-disabled') === 'true') return;
        const productId = addBtn.dataset.wsAdd;
        const entries = [...this.pending].filter(([, item]) => item.productId === productId);
        this.enqueueAdd(entries.length ? [[productId, entries]] : [], addBtn, productId);
        return;
      }
      const addAll = event.target.closest('[data-ws-add-all]');
      if (addAll) {
        if (addAll.getAttribute('aria-disabled') === 'true') return;
        this.enqueueAdd(WC.groupByProduct(this.pending), addAll, null);
        return;
      }
      if (event.target.closest('[data-ws-clear]')) this.clearSelection();
    }

    // Re-apply pending quantities to rows that were (re)inserted by a filter swap or load-more.
    syncRows() {
      this.tbody.querySelectorAll('[data-ws-variant]').forEach((row) => {
        const item = this.pending.get(row.dataset.wsVariant);
        const input = row.querySelector('.ws-qty-input');
        if (input && item) input.value = item.qty;
        row.classList.toggle('ws-variant--pending', Boolean(item));
      });
      WC.convertCurrency(this.tbody.querySelectorAll('span.money'));
      this.renderSelection();
    }

    renderSelection() {
      let count = 0;
      let total = 0;
      let offscreen = 0;
      const productsWithQty = new Set();
      this.pending.forEach((item, id) => {
        count += item.qty;
        total += item.qty * item.price;
        productsWithQty.add(item.productId);
        if (!this.rowFor(id)) offscreen += item.qty;
      });

      this.querySelector('[data-ws-sel-count]').textContent = WC.pluralise(count, this.strings);
      const selTotal = this.querySelector('[data-ws-sel-total]');
      selTotal.innerHTML = WC.money(total);
      WC.convertCurrency([selTotal]);
      const note = this.querySelector('[data-ws-sel-note]');
      note.hidden = offscreen === 0;
      note.textContent = offscreen ? `(${this.strings.offscreen.replace('[count]', offscreen)})` : '';
      this.querySelector('[data-ws-clear]').hidden = count === 0;

      // aria-disabled (not `disabled`) so the button that was just clicked keeps keyboard focus.
      this.querySelector('[data-ws-add-all]').setAttribute('aria-disabled', String(count === 0 || this.busy));
      this.tbody.querySelectorAll('[data-ws-add]').forEach((btn) => {
        btn.setAttribute('aria-disabled', String(!productsWithQty.has(btn.dataset.wsAdd) || this.busy));
      });
    }

    clearSelection() {
      this.pending.clear();
      this.tbody.querySelectorAll('.ws-qty-input').forEach((input) => {
        if (input.value === '0') return;
        input.value = 0;
        // Lets <quantity-input> refresh its minus-button state; onQty then drops the entry.
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      this.renderSelection();
    }

    // groups: [[productId, entries], ...]. scopeId: productId for a row button, null for "Add all".
    enqueueAdd(groups, trigger, scopeId) {
      this.showError(scopeId, '');
      if (!groups.length) return this.showError(scopeId, this.strings.nothingSelected);
      // The catch keeps one unexpected throw from poisoning the chain for every later add.
      this.chain = this.chain
        .then(() => this.runAdd(groups, trigger, scopeId))
        .catch((err) => {
          console.error('[wholesale-order] Unexpected error in add queue', err);
          this.showError(scopeId, this.strings.error);
        });
    }

    // One /cart/add request PER PRODUCT, in sequence. A multi-line add is rejected as a whole
    // when any line fails (e.g. an off-screen variant that sold out), so batching "Add all" into
    // one request would throw away every valid line along with the bad one.
    async runAdd(groups, trigger, scopeId) {
      this.setBusy(true, trigger);
      const drawer = document.querySelector('cart-drawer');
      const failures = [];
      let added = 0;
      try {
        for (const [productId, entries] of groups) {
          this.showError(productId, '');
          const message = await this.submitGroup(entries, drawer);
          if (message === null) {
            added += entries.reduce((sum, [, item]) => sum + item.qty, 0);
          } else {
            this.showError(productId, message);
            const name = entries[0][1].productTitle;
            failures.push(message.includes(name) ? message : `${name}: ${message}`);
          }
        }
      } finally {
        // Also after failures: the cart may have changed underneath us, so resync with the truth.
        await this.refreshCart({ broadcast: true });
        this.setBusy(false, trigger);
      }
      if (added) this.announce(this.strings.added.replace('[count]', added));
      // The bar lists every failure for "Add all": the failing product may not be on screen.
      if (scopeId === null && failures.length) this.showError(null, failures.join(' '));
    }

    // Returns null on success, or the shopper-facing error message.
    async submitGroup(entries, drawer) {
      const errors = WC.validate(entries, (id) => this.rowFor(id), this.strings);
      if (errors.length) return errors.join(' ');
      try {
        const data = await WC.add(entries, drawer, this.strings.error);
        entries.forEach(([id]) => {
          this.pending.delete(id);
          const input = this.rowFor(id)?.querySelector('.ws-qty-input');
          if (input) {
            input.value = 0;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
        WC.renderCartSections(data.sections, drawer);
        return null;
      } catch (err) {
        console.error('[wholesale-order] Add to cart failed', err.context || {}, err);
        return err.shopperMessage || this.strings.error;
      }
    }

    async refreshCart({ broadcast = false } = {}) {
      const errorEl = this.querySelector('[data-ws-bar-error]');
      try {
        const cart = await WC.fetchCart();
        this.applyCart(cart);
        if (errorEl.dataset.refreshError) {
          errorEl.hidden = true;
          delete errorEl.dataset.refreshError;
        }
        // Lets the drawer's own cart-drawer-items resync. It refetches its section once more,
        // which duplicates renderCartSections() — accepted to keep every cart listener in step.
        if (broadcast) publish(PUB_SUB_EVENTS.cartUpdate, { source: 'wholesale-order', cartData: cart });
      } catch (err) {
        console.error('[wholesale-order] Could not refresh cart totals', err);
        errorEl.textContent = this.strings.refreshError;
        errorEl.dataset.refreshError = 'true';
        errorEl.hidden = false;
      }
    }

    applyCart(cart) {
      const { byVariant, byProduct } = WC.summarise(cart);
      const none = { qty: 0, value: 0 };

      this.tbody.querySelectorAll('[data-ws-variant]').forEach((row) => {
        const line = byVariant.get(row.dataset.wsVariant) || none;
        row.dataset.inCart = line.qty;
        row.classList.toggle('ws-variant--in-cart', line.qty > 0);
        row.querySelector('[data-ws-qty]').textContent = line.qty;
        row.querySelector('[data-ws-value]').innerHTML = WC.money(line.value);
        // The stock / quantity-rule cap applies to the cart total, so what's addable shrinks as it fills.
        const input = row.querySelector('.ws-qty-input');
        if (input && row.dataset.limit !== undefined) {
          const remaining = Math.max(0, Number(row.dataset.limit) - line.qty);
          input.max = remaining;
          row.querySelector('[data-ws-limit-msg]').hidden = remaining > 0;
        }
        // A tier price depends on what's already in the cart, so re-price any pending qty.
        const item = this.pending.get(row.dataset.wsVariant);
        if (item) item.price = WC.unitPrice(row, item.qty);
      });
      this.tbody.querySelectorAll('[data-ws-product]').forEach((row) => {
        const line = byProduct.get(row.dataset.wsProduct) || none;
        row.querySelector('[data-ws-qty]').textContent = line.qty;
        row.querySelector('[data-ws-value]').innerHTML = WC.money(line.value);
      });

      this.querySelector('[data-ws-cart-count]').textContent = WC.pluralise(cart.item_count, this.strings);
      this.querySelector('[data-ws-cart-total]').innerHTML = WC.money(cart.total_price);
      WC.convertCurrency(this.querySelectorAll('span.money'));
      this.renderSelection();
    }

    setBusy(busy, trigger) {
      this.busy = busy;
      trigger.classList.toggle('loading', busy);
      trigger.setAttribute('aria-busy', String(busy));
      this.renderSelection();
    }

    // productId null = the bar. A product whose row isn't on screen has nowhere to show an
    // error, which is why runAdd also collects failures into the bar for "Add all".
    showError(productId, message) {
      const target = productId
        ? this.tbody.querySelector(`[data-ws-error="${CSS.escape(productId)}"]`)
        : this.querySelector('[data-ws-bar-error]');
      if (!target) return;
      const holder = target.querySelector('.ws-error') || target;
      holder.textContent = message;
      target.hidden = !message;
      if (!productId) delete target.dataset.refreshError;
    }

    announce(message) {
      const status = this.querySelector('[data-ws-status]');
      status.textContent = message;
      clearTimeout(this.announceTimer);
      this.announceTimer = setTimeout(() => { status.textContent = ''; }, 4000);
    }
  }

  customElements.define('wholesale-order', WholesaleOrder);
}
